import { Modal } from '../../core/index.js'
import type { ImportPreview } from './importPreview.js'
import { Upload } from 'lucide-react'
import { Button } from '../../core/index.js'

interface ImportPreviewModalProps {
  title: string
  fileName: string
  preview: ImportPreview
  noun: [singular: string, plural: string]
  /** A reason this file must not be imported as it stands; disables the import. */
  blocked: string | null
  isImporting: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Shows what a chosen file contains before anything is sent - the import
 * only happens from here. The whole file is listed (it scrolls) rather than
 * a sample, since the point is to catch the wrong file or a bad row.
 */
export function ImportPreviewModal({ title, fileName, preview, noun, blocked, isImporting, onConfirm, onCancel }: ImportPreviewModalProps) {
  const count = preview.rows.length
  const countLabel = `${count.toLocaleString()} ${count === 1 ? noun[0] : noun[1]}`
  const reason = preview.problem ?? (count === 0 ? 'The file has no records to import.' : blocked)

  return (
    <Modal
      eyebrow="Preview"
      title={title}
      subtitle={
        <>
          <span className="font-mono">{fileName}</span>
          {!preview.problem && <span> · {countLabel}</span>}
        </>
      }
      canClose={!isImporting}
      onClose={onCancel}
      footer={
        <>
          {reason && !preview.problem && <p className="text-[11px] font-bold text-coral mb-3">{reason}</p>}
          <div className="flex items-center gap-3">
            <Button icon={Upload} autoFocus disabled={Boolean(reason) || isImporting} onClick={onConfirm}>
              {isImporting ? 'Importing…' : `Import ${countLabel}`}
            </Button>
            <button type="button" onClick={onCancel} disabled={isImporting} className="text-xs font-bold text-marine disabled:opacity-60">
              Cancel
            </button>
          </div>
        </>
      }
    >
      {preview.problem ? (
        <div className="p-6 text-sm text-coral font-bold">{preview.problem}</div>
      ) : (
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-white">
            <tr className="border-b border-marine/15 text-[10px] uppercase tracking-wide text-ink-mid">
              <th className="px-3 py-2 font-bold w-10">#</th>
              {preview.columns.map((column) => (
                <th key={column} className="px-3 py-2 font-bold whitespace-nowrap">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((row, index) => (
              // Rows have no identity of their own before import; position in the file is it.
              <tr key={index} className="border-b border-marine/10">
                <td className="px-3 py-1.5 text-ink-mid">{index + 1}</td>
                {preview.columns.map((column, columnIndex) => (
                  <td key={column} className="px-3 py-1.5 text-ink whitespace-nowrap">
                    {row[columnIndex] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  )
}
